import React from 'react'
import PropTypes from 'prop-types'
import { GridCell } from '@progress/kendo-react-grid'
import { TdActions } from '@/components/tdActions'
import Icon from '@/components/Icon'
import { Link } from 'react-router-dom'
import { Button } from 'reactstrap'
import { getStore } from '@/store'
import { actionCreators as dialogActions } from '@/store/Dialogs'
import { dialogTypes } from '@/dialogs'
import classnames from 'classnames'
import { localized } from '@/decorators/localized'

function multipleDetailDeleteColumnProps (detailUrl, confirmationModal) {
  class CommandCell extends GridCell {
    static propTypes = {
      detailUrl: PropTypes.func,
      confirmationModal: PropTypes.func
    }

    render () {
      const { dataItem } = this.props
      return (
        <TdActions>
          <Link className='btn-icon' to={detailUrl(dataItem)}><Icon name='pencil' /></Link>
          <button className='btn-icon' onClick={() => { confirmationModal(this.props.dataItem.id) }}><Icon name='x' />
          </button>
        </TdActions>
      )
    }
  }

  return {
    field: 'id',
    title: ' ',
    sortable: false,
    filterable: false,
    width: 45,
    cell: CommandCell
  }
}

function commandColumnProps ({ detailUrl,
  onDetailClick,
  onDisableClick,
  onEnableClick,
  canToggleActivation,
  itemIsActive,
  deleteAction,
  title,
  deleteTitleKey,
  deleteContentKey,
  deleteDisabled,
  editAction,
  editDisabled,
  additionalButtons }) {
  @localized()
  class CommandCell extends GridCell {
    render () {
      const { dataItem, localize } = this.props
      const url = detailUrl ? detailUrl(dataItem) : null
      const actionButtons = (typeof additionalButtons === 'function')
        ? additionalButtons(this.props.dataItem) // support for per-item conditional buttons
        : additionalButtons

      const isDeleteDisabled = typeof deleteDisabled === 'function' ? deleteDisabled(dataItem) : deleteDisabled
      const isEditDisabled = typeof editDisabled === 'function' ? editDisabled(dataItem) : editDisabled

      return (
        <TdActions>
          {url && <Link className='btn-icon' to={url}><Icon name='show' /></Link>}
          {onDetailClick &&
          <button className='btn-icon' onClick={() => onDetailClick(dataItem)}><Icon name='show' /></button>}
          {canToggleActivation && onDisableClick && itemIsActive(dataItem) &&
          <button className='btn-icon' onClick={() => onDisableClick(dataItem)}><Icon name='power-off power-off--off' /></button>}
          {canToggleActivation && onEnableClick && !itemIsActive(dataItem) &&
          <button className='btn-icon' onClick={() => onEnableClick(dataItem)}><Icon name='power-off power-off--on' /></button>}
          {!isEditDisabled && editAction && <button
            disabled={isEditDisabled}
            className='btn-icon'
            title={localize('Common.Edit')}
            onClick={() => editAction(dataItem)}>
            <Icon name='pencil' />
          </button>}
          {!isDeleteDisabled && deleteAction && <React.Fragment>
            <button
              disabled={isDeleteDisabled}
              className='btn-icon'
              title={deleteTitleKey ? localize(deleteTitleKey) : null}
              onClick={() => getStore().dispatch(dialogActions.openDialog({
                dialogType: dialogTypes.confirm,
                title: deleteTitleKey || 'Common.DeleteDialogTitle',
                content: deleteContentKey || 'Common.DeleteDialogText',
                onAction: () => deleteAction(dataItem)
              }))}>
              <Icon name='x' />
            </button>
          </React.Fragment>}
          {actionButtons && actionButtons.length > 0 && actionButtons
            .filter(b => !(this.props.dataItem.readonly || b.readonly) || b.readonlybutton === 'true')
            .map((b, i) => <ActionButton key={i} {...b} dataItem={this.props.dataItem} dataIndex={this.props.dataIndex} />)}
        </TdActions>
      )
    }
  }

  return {
    field: 'id',
    title: title || ' ',
    sortable: false,
    filterable: false,
    width: 45,
    cell: CommandCell
  }
}

function actionButtonsColumnProps (buttons, title = ' ') {
  class ActionButtonsCell extends GridCell {
    render () {
      let actionButtons = (typeof buttons === 'function')
        ? buttons(this.props.dataItem) // support for per-item conditional buttons
        : buttons
      // Filter the action buttons based on the 'show' function
      actionButtons = actionButtons.filter(button => {
        if (typeof button.show === 'function') {
          return button.show(this.props.dataItem)
        }
        return true
      })

      if (actionButtons && actionButtons.length > 0) {
        return (<TdActions>
          {actionButtons
            .filter(b => !this.props.dataItem.readonly || b.readonlybutton === 'true')
            .map((b, i) => <ActionButton key={i} {...b} dataItem={this.props.dataItem} dataIndex={this.props.dataIndex} />)}
        </TdActions>)
      }
      return null
    }
  }
  const ret = {
    field: 'id',
    title: title,
    sortable: false,
    filterable: false,
    cell: ActionButtonsCell
  }
  return title === ' ' ? { ...ret, width: 45 } : ret
}

function ActionButton ({ dataItem, dataIndex, icon, url, onClick, onConfirm, confirmTitle, confirmText, onDelete, className, iconClassName, disabled, show, ...rest }) {
  if (typeof show === 'function') {
    if (!show(dataItem)) {
      return null
    }
  }
  if (typeof icon === 'function') {
    icon = icon(dataItem)
  }
  if (typeof disabled === 'function') {
    disabled = disabled(dataItem)
  }
  className = classnames('btn-icon', className, { 'disabled': disabled })
  if (typeof url === 'function') {
    url = url(dataItem)
  }
  if (typeof url === 'string') {
    return <Link className={className} to={url}><Icon name={icon} className={iconClassName} {...rest} /></Link>
  }
  if (typeof onClick === 'function') {
    return <button className={className} onClick={(e) => {
      e.preventDefault()
      onClick(dataItem, dataIndex)
    }} disabled={disabled} {...rest}><Icon name={icon} className={iconClassName} /></button>
  }

  if (typeof onDelete === 'function') { // on delete is just a shortcut for pre-configured onConfirm
    if (!confirmTitle) confirmTitle = 'Common.DeleteDialogTitle'
    if (!confirmText) confirmText = 'Common.DeleteDialogText'
    onConfirm = onDelete
    if (!icon) icon = 'x danger'
  }
  if (typeof onConfirm === 'function') {
    if (typeof confirmTitle === 'function') {
      confirmTitle = confirmTitle(dataItem)
    }
    if (typeof confirmText === 'function') {
      confirmText = confirmText(dataItem)
    }
    return <button className={className} disabled={disabled} onClick={(e) => {
      e.preventDefault()
      getStore().dispatch(dialogActions.openDialog({
        dialogType: dialogTypes.confirm,
        title: confirmTitle,
        content: confirmText,
        onAction: () => onConfirm(dataItem, dataIndex)
      }))
    }} {...rest}>
      <Icon name={icon} className={iconClassName} />
    </button>
  }
  return null
}

function commandDeleteColumnProps (callback, props = {}) {
  class CommandCell extends GridCell {
    static propTypes = {
      detailUrl: PropTypes.func
    }

    delete = (e) => {
      e.preventDefault()
      this.props.delete(e)
    }

    render () {
      return (
        <TdActions>
          <Button disabled={props.disabled} className='btn-icon' onClick={() => { callback(this.props.dataItem.id) }}><Icon name='x danger' /></Button>
        </TdActions>
      )
    }
  }

  return {
    field: 'id',
    title: '',
    sortable: false,
    filterable: false,
    width: 45,
    cell: CommandCell
  }
}

export {
  multipleDetailDeleteColumnProps,
  commandColumnProps,
  actionButtonsColumnProps,
  commandDeleteColumnProps
}



// WEBPACK FOOTER //
// ./src/components/BsrsGrid/commands.js